import { AlertTriangle, Shield, TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { formatRiskScore, getRiskLevel } from '@/utils/formatters'

interface RiskScoreCardProps {
  score: number
  previousScore?: number
  totalFindings: number
  criticalCount: number
  highCount: number
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export function RiskScoreCard({
  score,
  previousScore,
  totalFindings,
  criticalCount,
  highCount,
  className,
  size = 'md',
}: RiskScoreCardProps) {
  const riskLevel = getRiskLevel(score)
  const hasImproved = previousScore !== undefined && score < previousScore
  const hasWorsened = previousScore !== undefined && score > previousScore

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950 dark:border-red-800'
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-950 dark:border-orange-800'
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-950 dark:border-yellow-800'
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-950 dark:border-green-800'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-950 dark:border-gray-800'
    }
  }

  const getRiskIcon = () => {
    switch (riskLevel) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="h-5 w-5" />
      case 'medium':
        return <AlertTriangle className="h-5 w-5" />
      case 'low':
        return <Shield className="h-5 w-5" />
      default:
        return <Shield className="h-5 w-5" />
    }
  }

  const sizeClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  }

  const titleSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  }

  const scoreSizes = {
    sm: 'text-2xl',
    md: 'text-3xl',
    lg: 'text-4xl',
  }

  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <CardHeader className={sizeClasses[size]}>
        <CardTitle className={cn('flex items-center justify-between', titleSizes[size])}>
          <span className="flex items-center gap-2">
            {getRiskIcon()}
            Risk Score
          </span>
          {previousScore !== undefined && (
            <div className="flex items-center gap-1 text-sm font-normal">
              {hasImproved && (
                <>
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">
                    -{Math.abs(score - previousScore).toFixed(1)}
                  </span>
                </>
              )}
              {hasWorsened && (
                <>
                  <TrendingUp className="h-4 w-4 text-red-600" />
                  <span className="text-red-600">
                    +{Math.abs(score - previousScore).toFixed(1)}
                  </span>
                </>
              )}
              {!hasImproved && !hasWorsened && (
                <span className="text-gray-500">No change</span>
              )}
            </div>
          )}
        </CardTitle>
        <CardDescription>
          Overall security risk assessment
        </CardDescription>
      </CardHeader>

      <CardContent className={cn(sizeClasses[size], 'pt-0')}>
        <div className="flex items-center justify-between">
          {/* Risk Score Display */}
          <div className="flex items-baseline gap-2">
            <span className={cn('font-bold', scoreSizes[size])}>
              {formatRiskScore(score)}
            </span>
            <Badge 
              variant="outline" 
              className={cn('capitalize', getRiskLevelColor(riskLevel))}
            >
              {riskLevel} risk
            </Badge>
          </div>

          {/* Visual Risk Indicator */}
          <div className="flex flex-col items-end gap-2">
            <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={cn(
                  'h-full transition-all duration-500 ease-out',
                  riskLevel === 'critical' ? 'bg-red-500' :
                  riskLevel === 'high' ? 'bg-orange-500' :
                  riskLevel === 'medium' ? 'bg-yellow-500' :
                  'bg-green-500'
                )}
                style={{ width: `${(score / 10) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {Math.round((score / 10) * 100)}%
            </span>
          </div>
        </div>

        {/* Findings Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {totalFindings} findings
            </span>
            <div className="flex items-center gap-3">
              {criticalCount > 0 && (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    {criticalCount} critical
                  </span>
                </span>
              )}
              {highCount > 0 && (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span className="text-orange-600 dark:text-orange-400 font-medium">
                    {highCount} high
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Risk Level Explanation */}
        <div className="mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {riskLevel === 'critical' && 'Immediate action required. Critical vulnerabilities found.'}
            {riskLevel === 'high' && 'High priority security issues that should be addressed soon.'}
            {riskLevel === 'medium' && 'Moderate risk level. Review and fix when possible.'}
            {riskLevel === 'low' && 'Low risk. Minimal security concerns detected.'}
          </p>
        </div>
      </CardContent>

      {/* Risk Level Accent Border */}
      <div
        className={cn(
          'absolute top-0 right-0 w-1 h-full',
          riskLevel === 'critical' ? 'bg-red-500' :
          riskLevel === 'high' ? 'bg-orange-500' :
          riskLevel === 'medium' ? 'bg-yellow-500' :
          'bg-green-500'
        )}
      />
    </Card>
  )
}